import React, { ReactNode, useMemo } from "react";
import { ChakraProvider, ChakraProviderProps } from "@chakra-ui/react";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { getCspNonce } from "../utils/cspNonce";

/**
 * A ChakraProvider wrapper that configures Emotion with the CSP nonce.
 *
 * This allows Chakra UI's CSS-in-JS styles to work with strict Content Security
 * Policies that use nonces instead of 'unsafe-inline'.
 *
 * The nonce is read from the <meta name="csp-nonce"> tag set by Rails.
 */
function ChakraProviderWithNonce({
  children,
  ...chakraProps
}: ChakraProviderProps & { children: ReactNode }) {
  const emotionCache = useMemo(() => {
    const nonce = getCspNonce();

    // IMPORTANT: Use "css" as the key to maintain backward compatibility with
    // existing CSS overrides in chakra-theme-overrides.scss that target .css-* classes.
    // Changing this to "chakra" would generate .chakra-* classes instead, breaking
    // all the Chakra component styling overrides.
    // Disable speedy mode (insertRule) to ensure CSS text is written to <style>
    // tag content. In speedy mode, CSS is only in the CSSOM and <style> tags are
    // empty. Turbo Drive's head merge deduplicates identical empty tags and can
    // remove them, losing the CSSOM rules. With speedy disabled, each tag has
    // unique text content and won't be deduped.
    return createCache({
      key: "css",
      nonce: nonce,
      speedy: false,
    });
  }, []);

  return (
    <CacheProvider value={emotionCache}>
      <ChakraProvider {...chakraProps}>{children}</ChakraProvider>
    </CacheProvider>
  );
}

export default ChakraProviderWithNonce;
